import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bell, UserCheck, MessagesSquare, Scale, ClipboardList, Building2 } from 'lucide-react';
import { ifpsApi } from '@/services/api.js';
import { Card, CardHeader, StatCard, QuickAction, EmptyState, ErrorPanel } from '@/components/common/Card.jsx';
import StatusBadge from '@/components/common/StatusBadge.jsx';
import { fmtRelative } from '@/utils/format.js';

export default function IfpsHome() {
  const { t } = useTranslation();
  const escalationsQuery = useQuery({
    queryKey: ['ifps', 'escalations', 'open'],
    queryFn:  () => ifpsApi.listEscalations({ status: 'open', per_page: 6 }),
    refetchInterval: 30000,
    retry: 1,
  });

  const disputesQuery = useQuery({
    queryKey: ['ifps', 'disputes', 'open'],
    queryFn:  () => ifpsApi.listDisputes({ status: 'open', per_page: 6 }),
    retry: 1,
  });

  const escalations = escalationsQuery.data?.data ?? [];
  const disputes    = disputesQuery.data?.data    ?? [];

  return (
    <>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t('home.ifpsHome.ifpsSupervisorDashboard')}</h1>
        <p className="mt-1.5 text-sm text-slate-600">{t('home.ifpsHome.identityStateOversightEscalationsAndDisputes')}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <StatCard label={t('home.ifpsHome.openEscalations')}  value={escalationsQuery.isLoading ? '…' : escalations.length} hint={t('home.ifpsHome.awaitingSupervisor')}   accent="rose" />
        <StatCard label={t('home.ifpsHome.activeDisputes')}   value={disputesQuery.isLoading ? '…' : disputes.length}       hint={t('common.statusInReview')}             accent="amber" />
        <StatCard label={t('home.ifpsHome.channelsLive')}     value="OK"                                                    hint={t('home.ifpsHome.realTimeWired')}       accent="sky" />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-10">
        <QuickAction to="/ifps/challenges"  title={t('home.ifpsHome.challengeCentre')}     description={t('home.ifpsHome.issueMonitorAndReviewIdentityChallenges')}                icon={Bell} />
        <QuickAction to="/ifps/identities"  title={t('home.ifpsHome.identityStates')}      description={t('home.ifpsHome.stateMachineViewPerIndividual')}                             icon={UserCheck} />
        <QuickAction to="/ifps/disputes"    title={t('home.ifpsHome.disputes')}             description={t('home.ifpsHome.guidedSignedDisputeWorkflow')}                               icon={MessagesSquare} />
        <QuickAction to="/ifps/escalations" title={t('home.ifpsHome.escalations')}          description={t('home.ifpsHome.highSeverityEventsNeedingSupervisorInput')}                 icon={Scale} />
        <QuickAction to="/ifps/queue"       title={t('home.ifpsHome.adjudicationQueue')}   description={t('home.ifpsHome.humanReviewQueueWithSlaClaim')}              icon={ClipboardList} />
        <QuickAction to="/ifps/partners"    title={t('home.ifpsHome.institutionalPartners')} description={t('home.ifpsHome.bankAddendumTierElectionsSafeEntity')} icon={Building2} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('home.ifpsHome.recentEscalations')} description={t('home.ifpsHome.mostRecentItemsRoutedToThe')} />
          <div className="mt-5">
            {escalationsQuery.isLoading ? (
              <SkeletonList rows={5} />
            ) : escalationsQuery.isError ? (
              <ErrorPanel error={escalationsQuery.error} onRetry={escalationsQuery.refetch} />
            ) : escalations.length === 0 ? (
              <EmptyState title={t('home.ifpsHome.noOpenEscalations')} description={t('home.ifpsHome.patternAnomaliesWillSurfaceHereWhen')} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {escalations.map(e => (
                  <li key={e.id} className="flex items-center gap-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">{e.title || e.reason || 'Escalation'}</div>
                      <div className="text-xs text-slate-500">{fmtRelative(e.created_at)}</div>
                    </div>
                    <StatusBadge status={e.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title={t('home.ifpsHome.activeDisputes')} description={t('home.ifpsHome.currentDisputeWorkflowItems')} />
          <div className="mt-5">
            {disputesQuery.isLoading ? (
              <SkeletonList rows={5} />
            ) : disputesQuery.isError ? (
              <ErrorPanel error={disputesQuery.error} onRetry={disputesQuery.refetch} />
            ) : disputes.length === 0 ? (
              <EmptyState title={t('home.ifpsHome.noActiveDisputes')} description={t('home.ifpsHome.disputesOpenedByIndividualsWillAppear')} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {disputes.map(d => (
                  <li key={d.id} className="flex items-center gap-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">{d.subject_summary || d.title || 'Dispute'}</div>
                      <div className="text-xs text-slate-500">{fmtRelative(d.created_at)}</div>
                    </div>
                    <StatusBadge status={d.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}

function SkeletonList({ rows = 3 }) {
  return (
    <ul className="divide-y divide-slate-100">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 py-3">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-1/2 rounded-full bg-slate-200 animate-pulse" />
            <div className="h-2.5 w-1/3 rounded-full bg-slate-100 animate-pulse" />
          </div>
          <div className="h-5 w-16 rounded-full bg-slate-100 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}
